// local-vault.js — v4.9.0 : coffre local chiffré au repos.
//
// Modèle :
// - une DEK (Data Encryption Key) aléatoire 256 bits chiffre les données locales ;
// - la DEK n'est jamais stockée en clair ;
// - elle est enveloppée avec une KEK dérivée du code du coffre par Argon2id ;
// - la DEK déchiffrée n'existe qu'en mémoire vive pendant la session ;
// - chaque enregistrement utilise crypto_secretbox (XSalsa20-Poly1305) et un
//   nonce aléatoire indépendant ;
// - des tags HMAC-like (BLAKE2b keyed) permettent les index sans révéler les
//   pseudos/groupIds dans IndexedDB.
//
// Ce module n'envoie strictement rien au serveur.
import { requireSodium, toB64, fromB64 } from './crypto.js';

const META_KEY = 'meta';
const VAULT_VERSION = 1;
let masterKey = null;

function sodium() { return requireSodium(); }
function wipe(bytes) { try { if (bytes) sodium().memzero(bytes); } catch (_) {} }

export function isVaultUnlocked() { return !!masterKey; }
export function lockVaultMemory() { wipe(masterKey); masterKey = null; }

function requireMasterKey() {
  if (!masterKey) {
    const error = new Error('Coffre local verrouillé.');
    error.name = 'VaultLockedError';
    throw error;
  }
  return masterKey;
}

// KDF : mémoire INTERACTIVE (64 MiB) + coût CPU MODERATE. Ce compromis
// évite les allocations 256 MiB fragiles dans Safari/iOS tout en gardant un
// coût offline sérieux. Les paramètres exacts sont persistés dans le metadata.
function kdfParams(meta = null) {
  const s = sodium();
  const opslimit = Number(meta?.opslimit || s.crypto_pwhash_OPSLIMIT_MODERATE);
  const memlimit = Number(meta?.memlimit || s.crypto_pwhash_MEMLIMIT_INTERACTIVE);
  return { opslimit, memlimit, alg: s.crypto_pwhash_ALG_ARGON2ID13 };
}

function deriveKek(code, salt, params = null) {
  const s = sodium();
  const value = String(code || '');
  if (value.length < 8 || value.length > 128) throw new Error('Le code du coffre doit contenir entre 8 et 128 caractères.');
  const cfg = params || kdfParams();
  return s.crypto_pwhash(
    s.crypto_secretbox_KEYBYTES,
    value,
    salt,
    cfg.opslimit,
    cfg.memlimit,
    cfg.alg,
  );
}

export function createVaultMetadata(code) {
  const s = sodium();
  const salt = s.randombytes_buf(s.crypto_pwhash_SALTBYTES);
  const dek = s.randombytes_buf(s.crypto_secretbox_KEYBYTES);
  const params = kdfParams();
  const kek = deriveKek(code, salt, params);
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const wrapped = s.crypto_secretbox_easy(dek, nonce, kek);
  wipe(kek);
  lockVaultMemory();
  masterKey = new Uint8Array(dek);
  wipe(dek);
  return {
    key: META_KEY,
    v: VAULT_VERSION,
    kdf: 'argon2id13-mobile-hardened',
    opslimit: params.opslimit,
    memlimit: params.memlimit,
    cipher: 'crypto_secretbox-xsalsa20poly1305',
    salt: toB64(salt),
    nonce: toB64(nonce),
    wrappedKey: toB64(wrapped),
    createdAt: Date.now(),
  };
}

export function unlockFromMetadata(meta, code) {
  const s = sodium();
  if (!meta || meta.key !== META_KEY || meta.v !== VAULT_VERSION || !meta.salt || !meta.nonce || !meta.wrappedKey) {
    throw new Error('Métadonnées du coffre local invalides.');
  }
  const kek = deriveKek(code, fromB64(meta.salt), kdfParams(meta));
  let dek;
  try {
    dek = s.crypto_secretbox_open_easy(fromB64(meta.wrappedKey), fromB64(meta.nonce), kek);
  } catch (_) {
    throw new Error('Code du coffre incorrect.');
  } finally {
    wipe(kek);
  }
  if (!dek || dek.length !== s.crypto_secretbox_KEYBYTES) throw new Error('Coffre local corrompu.');
  lockVaultMemory();
  masterKey = new Uint8Array(dek);
  wipe(dek);
  return true;
}

export function wrapCurrentMasterKeyWithCode(meta, newCode) {
  const s = sodium();
  const dek = new Uint8Array(requireMasterKey());
  const salt = s.randombytes_buf(s.crypto_pwhash_SALTBYTES);
  const params = kdfParams();
  const kek = deriveKek(newCode, salt, params);
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  try {
    const wrapped = s.crypto_secretbox_easy(dek, nonce, kek);
    return {
      ...meta,
      kdf: 'argon2id13-mobile-hardened',
      opslimit: params.opslimit,
      memlimit: params.memlimit,
      cipher: 'crypto_secretbox-xsalsa20poly1305',
      salt: toB64(salt), nonce: toB64(nonce), wrappedKey: toB64(wrapped),
      changedAt: Date.now(),
    };
  } finally { wipe(kek); wipe(dek); }
}

export function rewrapVaultMetadata(meta, oldCode, newCode) {
  // Si le coffre est déjà ouvert, oldCode est vérifié en ré-ouvrant une copie
  // temporaire pour éviter qu'une page déjà déverrouillée change le code sans
  // connaître l'ancien secret.
  const current = masterKey ? new Uint8Array(masterKey) : null;
  try {
    unlockFromMetadata(meta, oldCode);
    const dek = new Uint8Array(requireMasterKey());
    const s = sodium();
    const salt = s.randombytes_buf(s.crypto_pwhash_SALTBYTES);
    const params = kdfParams();
    const kek = deriveKek(newCode, salt, params);
    const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
    const wrapped = s.crypto_secretbox_easy(dek, nonce, kek);
    wipe(kek); wipe(dek);
    return { ...meta, kdf: 'argon2id13-mobile-hardened', opslimit: params.opslimit, memlimit: params.memlimit, salt: toB64(salt), nonce: toB64(nonce), wrappedKey: toB64(wrapped), changedAt: Date.now() };
  } finally {
    if (current) { lockVaultMemory(); masterKey = current; }
  }
}



// v4.9.2 : seconde enveloppe facultative de la DEK avec un secret fourni par
// WebAuthn PRF. La credential/PRF reste gérée par webauthn-vault.js ; ce module
// ne connaît ni Face ID ni WebAuthn, il manipule uniquement un secret binaire.
function externalKek(rawSecret, label = 'webauthn-prf') {
  const s = sodium();
  const secret = rawSecret instanceof Uint8Array ? rawSecret : new Uint8Array(rawSecret || 0);
  if (secret.length < 16) throw new Error('Secret externe du coffre invalide.');
  const context = s.from_string(`messagerie-vault-external-kek:v1:${String(label)}`);
  const material = new Uint8Array(context.length + secret.length);
  material.set(context, 0); material.set(secret, context.length);
  const key = s.crypto_generichash(s.crypto_secretbox_KEYBYTES, material);
  wipe(material);
  return key;
}

export function wrapMasterKeyWithExternalSecret(rawSecret, label = 'webauthn-prf') {
  const s = sodium();
  const key = externalKek(rawSecret, label);
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  try {
    const wrapped = s.crypto_secretbox_easy(requireMasterKey(), nonce, key);
    return { v: 1, label: String(label), nonce: toB64(nonce), wrappedKey: toB64(wrapped) };
  } finally { wipe(key); }
}

export function unlockFromExternalSecret(wrap, rawSecret, expectedLabel = 'webauthn-prf') {
  const s = sodium();
  if (!wrap || wrap.v !== 1 || wrap.label !== String(expectedLabel) || !wrap.nonce || !wrap.wrappedKey) {
    throw new Error('Enveloppe biométrique du coffre invalide.');
  }
  const key = externalKek(rawSecret, expectedLabel);
  let dek;
  try {
    dek = s.crypto_secretbox_open_easy(fromB64(wrap.wrappedKey), fromB64(wrap.nonce), key);
  } catch (_) {
    throw new Error('Authentification système valide mais clé du coffre incompatible.');
  } finally { wipe(key); }
  if (!dek || dek.length !== s.crypto_secretbox_KEYBYTES) throw new Error('Clé du coffre biométrique corrompue.');
  lockVaultMemory(); masterKey = new Uint8Array(dek); wipe(dek);
  return true;
}

function scopeBytes(scope, key = '') {
  return sodium().from_string(JSON.stringify({ v: 1, scope: String(scope), key: String(key) }));
}

export function sealJson(scope, key, value) {
  const s = sodium();
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const payload = JSON.stringify({ v: 1, scope: String(scope), key: String(key), value });
  const box = s.crypto_secretbox_easy(s.from_string(payload), nonce, requireMasterKey());
  return { nonce: toB64(nonce), box };
}

export function openJson(scope, key, record, fallback = null) {
  const s = sodium();
  if (!record?.nonce || !record?.box) return fallback;
  let plain;
  try { plain = s.crypto_secretbox_open_easy(record.box, fromB64(record.nonce), requireMasterKey()); }
  catch (_) { throw new Error('Donnée locale chiffrée illisible ou altérée.'); }
  const parsed = JSON.parse(s.to_string(plain));
  if (parsed?.v !== 1 || parsed.scope !== String(scope) || parsed.key !== String(key)) {
    throw new Error('Donnée locale déplacée ou incohérente.');
  }
  return parsed.value;
}

export function sealBytes(scope, key, bytes) {
  const s = sodium();
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const raw = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || 0);
  // Prefixe authentifié dans le plaintext pour empêcher un échange silencieux
  // entre deux usages différents, secretbox n'ayant pas d'AAD.
  const prefix = scopeBytes(scope, key);
  const payload = new Uint8Array(4 + prefix.length + raw.length);
  new DataView(payload.buffer).setUint32(0, prefix.length);
  payload.set(prefix, 4); payload.set(raw, 4 + prefix.length);
  const box = s.crypto_secretbox_easy(payload, nonce, requireMasterKey());
  return { nonce: toB64(nonce), box };
}

export function openBytes(scope, key, record) {
  const s = sodium();
  if (!record?.nonce || !record?.box) return new Uint8Array();
  let payload;
  try { payload = s.crypto_secretbox_open_easy(record.box, fromB64(record.nonce), requireMasterKey()); }
  catch (_) { throw new Error('Média local chiffré illisible ou altéré.'); }
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const prefixLen = view.getUint32(0);
  if (prefixLen < 2 || 4 + prefixLen > payload.length) throw new Error('Média local chiffré corrompu.');
  const prefix = JSON.parse(s.to_string(payload.subarray(4, 4 + prefixLen)));
  if (prefix?.v !== 1 || prefix.scope !== String(scope) || prefix.key !== String(key)) throw new Error('Média local déplacé ou incohérent.');
  return payload.slice(4 + prefixLen);
}

export function localTag(namespace, value) {
  const s = sodium();
  const input = s.from_string(`${String(namespace)}\u0000${String(value)}`);
  return s.to_hex(s.crypto_generichash(16, input, requireMasterKey()));
}

// Chiffrement synchrone pour les quelques métadonnées locales historiquement
// en localStorage (historique d'appel / diagnostic réseau). Le format ne révèle
// ni pseudo ni valeur ; seul le nom de la clé localStorage reste visible.
export function sealLocalString(scope, plaintext) {
  const sealed = sealJson(`localStorage:${scope}`, '', String(plaintext ?? ''));
  return JSON.stringify({ v: 1, nonce: sealed.nonce, box: toB64(sealed.box) });
}
export function openLocalString(scope, serialized) {
  if (!serialized) return '';
  let parsed;
  try { parsed = JSON.parse(serialized); } catch (_) { return String(serialized); } // legacy plaintext
  if (parsed?.v !== 1 || !parsed?.nonce || !parsed?.box) return String(serialized); // legacy JSON plaintext
  return String(openJson(`localStorage:${scope}`, '', { nonce: parsed.nonce, box: fromB64(parsed.box) }, ''));
}

// Utilisé uniquement par les tests unitaires Node. N'est jamais appelé par l'app.
export function __setVaultKeyForTests(bytes) {
  lockVaultMemory();
  masterKey = new Uint8Array(bytes);
}
